import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useChatStore,
  Conversation as ChatStoreConversation,
} from '../components/Pleri/store/chatStore';
import { ToolExecutionPanel } from '../components/Pleri/components/ToolExecution/ToolExecutionPanel';
import {
  useGetConversation,
  useMarkFollowUpAsRead,
  useListConversations,
} from '../api/conversations';
import { ChatArea } from '../components/Pleri/components/ChatArea/ChatArea';
import RefreshButton from '../components/common/RefreshButton';
import { LoadingSpinner } from '../components/Pleri/components/ToolExecution/LoadingSpinner';

import { useTranslation } from 'react-i18next';

// Accept optional conversationId, headerText, autoRefresh, and autoScrollToBottom props
export const ConversationView: React.FC<{
  conversationId?: string;
  headerText?: string;
  autoRefresh?: boolean;
  showMessageFromIndex?: number;
  autoScrollToBottom?: boolean;
}> = ({
  conversationId: propConversationId,
  headerText = 'Read-only conversation view',
  autoRefresh = false,
  showMessageFromIndex = 0,
  autoScrollToBottom = true,
}) => {
  const { t } = useTranslation('pleri/pages');
  const params = useParams<{ conversationId: string }>();
  const conversationId = propConversationId || params.conversationId || '';
  const [error, setError] = useState<string | null>(null);

  // Select only what's needed — a scalar selector avoids creating a new object on every render,
  // which would otherwise re-render this component on every Zustand store update.
  const setActiveConversation = useChatStore(
    (state) => state.setActiveConversation,
  );

  const { mutate: markFollowUpAsRead } = useMarkFollowUpAsRead();
  const { data: conversationsData } = useListConversations();

  // Keep latest refs so effects can read current values without taking them as deps.
  // This breaks the loop: markFollowUpAsRead → invalidateQueries(conversationKeys.all)
  // → conversationsData re-fetches → effect re-runs → markFollowUpAsRead is called again.
  const markFollowUpAsReadRef = useRef(markFollowUpAsRead);
  markFollowUpAsReadRef.current = markFollowUpAsRead;
  const conversationsDataRef = useRef(conversationsData);
  conversationsDataRef.current = conversationsData;

  // Use the existing React Query hook to fetch conversation data
  const {
    data: conversationData,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useGetConversation(conversationId);

  const isToolPanelOpen = useChatStore((state) => {
    const activeConversation = state.activeConversationId
      ? state.conversations[state.activeConversationId]
      : null;
    return activeConversation?.toolPanelOpen || false;
  });

  const setToolPanelOpen = useChatStore((state) => state.setToolPanelOpen);

  // Function to toggle tool panel
  const toggleToolPanel = () => {
    if (conversationId) {
      setToolPanelOpen(conversationId, !isToolPanelOpen);
    }
  };

  useEffect(() => {
    if (!conversationId) {
      return;
    }

    if (conversationData && !isLoading && !isError) {
      useChatStore.setState((state) => {
        const conversation: ChatStoreConversation = {
          id: conversationId,
          messages: conversationData.messages || [],
          title: (conversationData.meta?.title as string) || t('conversation'),
          createdAt: new Date(conversationData.createdAt).getTime(),
          updatedAt: new Date(conversationData.lastUpdated).getTime(),
          statusUpdatedAt: new Date(conversationData.statusUpdatedAt).getTime(),
          isLoading: false,
          pendingToolUses: new Set<string>(),
          toolPanelOpen:
            state.conversations[conversationId]?.toolPanelOpen ?? false,
          enableReasoning:
            Boolean(conversationData.meta?.enableReasoning) || false,
          scope: conversationData.scope,
        };
        const updatedConversations = { ...state.conversations };
        updatedConversations[conversationId] = conversation;

        return {
          conversations: updatedConversations,
          activeConversationId: conversationId || null,
        };
      });
      setActiveConversation({ conversationId });
    }
  }, [
    conversationId,
    setActiveConversation,
    conversationData,
    isLoading,
    isError,
    t,
  ]);

  // Separate effect for marking follow-up as read. Uses refs for markFollowUpAsRead and
  // conversationsData so that the mutation's onSuccess (which invalidates conversationKeys.all
  // and causes conversationsData to refetch) does not re-trigger this effect and create a loop.
  useEffect(() => {
    if (!conversationId || !conversationData || isLoading || isError) {
      return;
    }
    const conversation = conversationsDataRef.current?.conversations?.find(
      (conv) => conv.conversationId === conversationId,
    );
    if (conversation?.followUpNotify) {
      markFollowUpAsReadRef.current(conversationId);
    }
  }, [conversationId, conversationData, isLoading, isError]);

  // Set error state if the query fails
  useEffect(() => {
    if (isError) {
      setError(t('failedToLoadConversationPleaseTryAgain'));
    } else {
      setError(null);
    }
  }, [isError, t]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) {
      return;
    }
    const interval = setInterval(() => {
      refetch();
    }, 2000);
    return () => clearInterval(interval);
  }, [autoRefresh, refetch]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center pt-20">
        <LoadingSpinner text={t('loadingConversation')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="max-w-md rounded-lg bg-red-50 p-6 text-center dark:bg-red-950">
          <p className="text-lg font-medium text-red-800">{error}</p>
          <p className="mt-2 text-sm text-red-600">
            {t('theConversationMayNotExistOrYou')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-white dark:bg-gray-900">
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {headerText}
          </h1>
          <RefreshButton
            onClick={() => refetch()}
            loading={isFetching}
            aria-label={t('refreshConversation')}
            title={t('refreshConversation')}
          />
        </div>
        <button
          onClick={toggleToolPanel}
          className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 dark:border-gray-600 dark:text-gray-400 dark:hover:text-gray-100"
        >
          {isToolPanelOpen ? t('hideTools') : t('showTools')}
        </button>
      </div>
      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-1 overflow-hidden">
          {/* ChatArea with custom input */}
          <div className="relative flex flex-1 flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto">
              <ChatArea
                isReadOnly={true}
                isCompact={false}
                showMessageFromIndex={showMessageFromIndex}
                preventToolUseBlockToCollapse={true}
                autoScrollToBottom={autoScrollToBottom}
                onToolExpand={() => {
                  if (!isToolPanelOpen && conversationId) {
                    setToolPanelOpen(conversationId, true);
                  }
                }}
              />
            </div>
          </div>

          {/* Tool execution panel */}
          {isToolPanelOpen && (
            <div className="sticky top-0 z-[100] h-full">
              <ToolExecutionPanel />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
